import { Spin } from 'antd';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import { formatCurrency } from '@/lib/utils';
import { FluxoCaixaEvolucaoMensal, FluxoCaixaResumo } from '../types/fluxoCaixa';

interface FluxoCaixaGraficosProps {
  resumo: FluxoCaixaResumo;
  evolucao: FluxoCaixaEvolucaoMensal[];
  loadingResumo?: boolean;
  loadingEvolucao?: boolean;
}

const CORES_PIZZA = ['#22c55e', '#ef4444'];

export function FluxoCaixaGraficos({
  resumo,
  evolucao,
  loadingResumo = false,
  loadingEvolucao = false,
}: FluxoCaixaGraficosProps) {
  const dadosPizza = [
    { name: 'Entradas', value: resumo.total_entrada, color: CORES_PIZZA[0] },
    { name: 'Saídas', value: resumo.total_saida, color: CORES_PIZZA[1] },
  ].filter(item => item.value > 0);

  const temDadosPizza = dadosPizza.length > 0;
  const temDadosBarras = evolucao.some(item => item.entrada > 0 || item.saida > 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="rounded-xl bg-white/5 border border-white/10 p-4 min-h-[260px]">
        <p className="text-xs text-white/60 uppercase tracking-wider font-semibold mb-3">
          Entrada vs Saída
        </p>
        {loadingResumo ? (
          <div className="flex justify-center items-center h-[200px]">
            <Spin />
          </div>
        ) : temDadosPizza ? (
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={dadosPizza}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
              >
                {dadosPizza.map(item => (
                  <Cell key={item.name} fill={item.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-white/50 text-center py-16">Sem dados no período</p>
        )}
      </div>

      <div className="rounded-xl bg-white/5 border border-white/10 p-4 min-h-[260px]">
        <p className="text-xs text-white/60 uppercase tracking-wider font-semibold mb-3">
          Evolução — últimos 3 meses
        </p>
        {loadingEvolucao ? (
          <div className="flex justify-center items-center h-[200px]">
            <Spin />
          </div>
        ) : temDadosBarras ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={evolucao} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
              <Tooltip
                formatter={(value: number) => formatCurrency(value)}
                contentStyle={{ borderRadius: 8, fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="entrada" name="Entradas" fill="#22c55e" radius={[4, 4, 0, 0]} />
              <Bar dataKey="saida" name="Saídas" fill="#ef4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-white/50 text-center py-16">Sem dados nos últimos 3 meses</p>
        )}
      </div>
    </div>
  );
}
